import * as React from 'react'

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '~/client/src/shared/components/shadcn-ui/tooltip'

interface IProps {
  /** Why the control inside is unavailable. */
  reason: string
  /** Whether it is actually denied right now; the wrapper is inert otherwise. */
  isDenied: boolean
  /**
   * Wrapper classes. Must stay width-neutral wherever a parent measures its children -
   * the controls selection bar re-measures only when the item COUNT changes, so a wrapper
   * that added width would mis-size the row silently.
   */
  className?: string
  /** `none` inside a `role="menu"`, so the wrapper cannot break menu -> menuitem ownership. */
  role?: 'none'
  children: React.ReactNode
}

/**
 * Explains why the control inside it is disabled.
 *
 * A disabled control carries `pointer-events: none` - from the Button's `tw:disabled:*` and
 * the menu item's `tw:data-disabled:*` - so it can never fire the hover that opens a
 * tooltip. Hit-testing skips a pointer-events-none element and lands on its ancestor; this
 * wrapper is that ancestor, and it is what carries the trigger. Composing the trigger onto
 * the control instead would silently never open: base-ui hands the trigger a ref, and the
 * controls here are not all forwardRef components under React 17.
 *
 * Gated through base-ui's own `disabled` rather than by unmounting, so the DOM shape is the
 * same whether or not the action is available.
 */
export function DisabledReasonTooltip({
  reason,
  isDenied,
  className = 'tw:inline-flex tw:shrink-0',
  role,
  children,
}: IProps) {
  return (
    <TooltipProvider>
      <Tooltip disabled={!isDenied}>
        <TooltipTrigger render={<span role={role} className={className} />}>
          {children}
        </TooltipTrigger>
        <TooltipContent className="tw:max-w-[280px]">{reason}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}
